import { useContext, useRef } from 'react';
import type { WatchQueryFetchPolicy } from 'apollo-client';

import { RoutesContext } from '@confluence/route-manager';
import { useIsSuperAdmin } from '@confluence/super-admin';

/**
 * Returns "network-only" fetching policy if current render caused by a transition
 * otherwise returns "cache-first" fetching policy.
 * On initial load it returns "cache-first" which allows to make use
 * of prefilled cache if page was SSRed.
 */
export function useRefetchOnTransition(): WatchQueryFetchPolicy {
	const { transitionId } = useContext(RoutesContext);
	const refPrevTransitionId = useRef<typeof transitionId>(0);
	// Note that "useIsSuperAdmin()" may do a GraphQL BE query - but only if a Super Admin Claim
	// cookie is found (that is, we might be in Super Admin mode) and is skipped otherwise. So most
	// page loads won't be affected by this check and are not slowed down.
	const { isSuperAdmin } = useIsSuperAdmin();

	// Admin Key mode (a.k.a., Super Admin) will allow access to restricted pages by setting
	// different permissions. If we try to go to a restricted page we don't have access to, we get
	// `null` restrictions data because we don't have access to it - and that ends up in the cache.
	// So when we are using Admin Key we need to refetch the restrictions data to be sure we're
	// getting the real data.
	const fetchPolicy =
		refPrevTransitionId.current === transitionId && !isSuperAdmin ? 'cache-first' : 'network-only';

	refPrevTransitionId.current = transitionId;

	return fetchPolicy;
}
